(() => {
  const TRACKING_ATTRIBUTE_PREFIX = 'tracking';
  const STORAGE_PREFIX = 'amea_tracking';
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  const SESSION_STORAGE_KEY = `${STORAGE_PREFIX}_session`;
  const VISITOR_STORAGE_KEY = `${STORAGE_PREFIX}_visitor_id`;

  function safeStorage(type) {
    try {
      const storage = window[type];
      const testKey = `${STORAGE_PREFIX}_test`;
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return undefined;
    }
  }

  const localStore = safeStorage('localStorage');
  const sessionStore = safeStorage('sessionStorage');

  function createId(prefix) {
    const randomValue =
      window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}_${randomValue}`;
  }

  function getOrCreateVisitorId() {
    const existingVisitorId = localStore?.getItem(VISITOR_STORAGE_KEY);
    if (existingVisitorId) return existingVisitorId;

    const visitorId = createId('visitor');
    localStore?.setItem(VISITOR_STORAGE_KEY, visitorId);
    return visitorId;
  }

  function readSession() {
    try {
      return JSON.parse(sessionStore?.getItem(SESSION_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function getOrCreateSession() {
    const now = Date.now();
    const currentSession = readSession();
    if (currentSession?.id && now - Number(currentSession.updated_at || 0) < SESSION_TIMEOUT_MS) {
      const updatedSession = {
        ...currentSession,
        updated_at: now,
      };
      sessionStore?.setItem(SESSION_STORAGE_KEY, JSON.stringify(updatedSession));
      return updatedSession;
    }

    const session = {
      id: createId('session'),
      started_at: now,
      updated_at: now,
    };
    sessionStore?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    return session;
  }

  function parseBrowser(userAgent) {
    if (/Edg\//.test(userAgent)) return 'Edge';
    if (/OPR\//.test(userAgent)) return 'Opera';
    if (/CriOS|Chrome\//.test(userAgent)) return 'Chrome';
    if (/FxiOS|Firefox\//.test(userAgent)) return 'Firefox';
    if (/Safari\//.test(userAgent)) return 'Safari';
    return 'Unknown';
  }

  function parseOs(userAgent) {
    if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
    if (/Android/.test(userAgent)) return 'Android';
    if (/Mac OS X|Macintosh/.test(userAgent)) return 'macOS';
    if (/Windows/.test(userAgent)) return 'Windows';
    if (/Linux/.test(userAgent)) return 'Linux';
    return 'Unknown';
  }

  function getDeviceType() {
    const userAgent = navigator.userAgent || '';
    if (/Mobi|Android|iPhone|iPod/.test(userAgent)) return 'mobile';
    if (/iPad|Tablet/.test(userAgent)) return 'tablet';
    return 'desktop';
  }

  function normalizeValue(value) {
    if (value === undefined || value === null || value === '') return undefined;
    if (value === 'true') return true;
    if (value === 'false') return false;

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && String(value).trim() !== '') return numericValue;

    return value;
  }

  function datasetToPayload(element) {
    if (!element) return {};

    return Object.entries(element.dataset).reduce((payload, [key, value]) => {
      if (!key.startsWith(TRACKING_ATTRIBUTE_PREFIX)) return payload;

      const payloadKey = key
        .replace(TRACKING_ATTRIBUTE_PREFIX, '')
        .replace(/^[A-Z]/, (letter) => letter.toLowerCase())
        .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      const normalizedValue = normalizeValue(value);

      if (payloadKey && normalizedValue !== undefined) payload[payloadKey] = normalizedValue;
      return payload;
    }, {});
  }

  function closestTrackingPayload(element) {
    const trackingElement = element?.closest?.('[data-tracking-product-id], [data-tracking-variant-id]');
    return datasetToPayload(trackingElement);
  }

  function push(eventName, payload = {}) {
    if (!eventName) return;
    const session = getOrCreateSession();
    const userAgent = navigator.userAgent || '';
    const shopifyContext = window.themeTrackingContext || {};

    const normalizedPayload = Object.entries(payload).reduce((accumulator, [key, value]) => {
      const normalizedValue = normalizeValue(value);
      if (normalizedValue !== undefined) accumulator[key] = normalizedValue;
      return accumulator;
    }, {});

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      event_id: createId(eventName),
      event_timestamp: new Date().toISOString(),
      visitor_id: getOrCreateVisitorId(),
      session_id: session.id,
      session_started_at: new Date(session.started_at).toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer || undefined,
      user_logged_in: shopifyContext.customer?.logged_in,
      user_id: shopifyContext.customer?.id,
      user_orders_count: shopifyContext.customer?.orders_count,
      user_accepts_marketing: shopifyContext.customer?.accepts_marketing,
      user_tags: shopifyContext.customer?.tags,
      device_type: getDeviceType(),
      browser_name: parseBrowser(userAgent),
      operating_system: parseOs(userAgent),
      user_agent: userAgent,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
      device_pixel_ratio: window.devicePixelRatio,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      shop_name: shopifyContext.shop?.name,
      shop_domain: shopifyContext.shop?.domain,
      country: shopifyContext.localization?.country,
      currency: shopifyContext.localization?.currency,
      ...normalizedPayload,
    });
  }

  function getPayloadFromElement(element, extraPayload = {}) {
    return {
      ...closestTrackingPayload(element),
      ...datasetToPayload(element),
      ...extraPayload,
    };
  }

  function payloadKeyToDataAttribute(key) {
    return `data-tracking-${key.replace(/_/g, '-')}`;
  }

  function applyPayloadToElement(element, payload = {}) {
    if (!element) return;

    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      element.setAttribute(payloadKeyToDataAttribute(key), value);
    });
  }

  window.themeTracking = {
    push,
    datasetToPayload,
    closestTrackingPayload,
    getPayloadFromElement,
    applyPayloadToElement,
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const link = target?.closest('[data-tracking-product-click]');
    if (!link) return;

    const payload = getPayloadFromElement(link, {
      url: link.href,
    });

    push('carousel_product_click', payload);
  });
})();
